import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormHelperText,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { MANUAL_REQUEST_DROPDOWNS } from '../../constants/constant';
import { validateCustomerNumber, validateAssignmentNumber } from '../../utils/validations';

const NewRequestModal = ({ open, onClose, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    companyCode: MANUAL_REQUEST_DROPDOWNS.companyCodes[0].value,
    customerNumber: '',
    assignmentNumber: ''
  });

  const [errors, setErrors] = useState({
    companyCode: '',
    customerNumber: '',
    assignmentNumber: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    let newErrors = {
      companyCode: '',
      customerNumber: '',
      assignmentNumber: ''
    };

    if (!formData.companyCode) {
      newErrors.companyCode = 'Company Code is required';
    }

    if (!formData.customerNumber) {
      newErrors.customerNumber = 'Customer Number is required';
    } else if (!validateCustomerNumber(formData.customerNumber)) {
      newErrors.customerNumber = 'Customer Number must be exactly 8 digits';
    }

    if (!formData.assignmentNumber) {
      newErrors.assignmentNumber = 'Assignment Number is required';
    } else if (!validateAssignmentNumber(formData.assignmentNumber)) {
      newErrors.assignmentNumber = 'Assignment Number must be exactly 10 digits';
    }

    setErrors(newErrors);
    return Object.values(newErrors).every(error => error === '');
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      await onSubmit(formData);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      companyCode: MANUAL_REQUEST_DROPDOWNS.companyCodes[0].value,
      customerNumber: '',
      assignmentNumber: ''
    });
    setErrors({
      companyCode: '',
      customerNumber: '',
      assignmentNumber: ''
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#333333', fontSize: '20px', paddingBottom: '24px', paddingLeft: '24px', paddingRight: '24px', paddingTop: '24px' }}>
        New Request
      </DialogTitle>

      <DialogContent sx={{ paddingTop: '0px', paddingBottom: '30px', paddingLeft: '24px', paddingRight: '24px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Company Code Dropdown */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <InputLabel sx={{ fontWeight: 600, color: '#333333', fontSize: '14px' }}>Company Code</InputLabel>
            <FormControl fullWidth error={!!errors.companyCode} size="small">
              <Select
                name="companyCode"
                value={formData.companyCode}
                onChange={handleInputChange}
                label=""
              >
                {MANUAL_REQUEST_DROPDOWNS.companyCodes.map((code) => (
                  <MenuItem key={code.value} value={code.value}>
                    {code.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.companyCode && (
                <FormHelperText>{errors.companyCode}</FormHelperText>
              )}
            </FormControl>
          </Box>

          {/* Customer Number */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <InputLabel sx={{ fontWeight: 600, color: '#333333', fontSize: '14px' }}>Customer Number</InputLabel>
            <TextField
              fullWidth
              name="customerNumber"
              type="text"
              inputProps={{ maxLength: 8, pattern: '[0-9]*' }}
              placeholder="Enter 8-digit customer number"
              value={formData.customerNumber}
              onChange={handleInputChange}
              error={!!errors.customerNumber}
              helperText={errors.customerNumber}
              variant="outlined"
              size="small"
            />
          </Box>

          {/* Assignment Number */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <InputLabel sx={{ fontWeight: 600, color: '#333333', fontSize: '14px' }}>Assignment Number</InputLabel>
            <TextField
              fullWidth
              name="assignmentNumber"
              type="text"
              inputProps={{ maxLength: 10, pattern: '[0-9]*' }}
              placeholder="Enter 10-digit assignment number"
              value={formData.assignmentNumber}
              onChange={handleInputChange}
              error={!!errors.assignmentNumber}
              helperText={errors.assignmentNumber}
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '20px 24px 24px 24px', gap: '12px', justifyContent: 'flex-end' }}>
        <Button onClick={handleClose} variant="outlined" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            backgroundColor: '#ffd800',
            color: '#000',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#e6c200'
            }
          }}
        >
          {isSubmitting ? 'Loading...' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewRequestModal;
